import * as CollapsiblePrimitive from "@radix-ui/react-collapsible"
import { CaretRightIcon } from "@phosphor-icons/react"
import type * as React from "react"

import { cn } from "~/utils/helpers"

/**
 * The steps an agent takes, one line per step. Port of shadcn/ui `trace` (radix-nova) onto
 * Hakio tokens. {@link ../design-system/intents.ts TRACE_INTENT}
 */
function Trace({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="trace"
      className={cn(
        "flex w-full min-w-0 flex-col gap-2 *:has-[>[data-slot=trace-line]]:flex *:has-[>[data-slot=trace-line]]:w-fit *:has-[>[data-slot=trace-line]]:max-w-full *:has-[>[data-slot=trace-line]]:min-w-0 *:has-[>[data-slot=trace-line]]:rounded-md *:has-[>[data-slot=trace-line]]:text-left *:has-[>[data-slot=trace-line]]:outline-hidden *:has-[>[data-slot=trace-line]]:focus-visible:shadow-borders-interactive-with-shadow",
        className,
      )}
      {...props}
    />
  )
}

type TraceStatus = "running" | "error" | "pending"

function TraceLine({
  className,
  status,
  ...props
}: React.ComponentProps<"span"> & { status?: TraceStatus }) {
  return (
    <span
      data-slot="trace-line"
      data-status={status}
      className={cn(
        "flex w-fit max-w-full min-w-0 items-center gap-2 text-xs text-fg-muted transition-colors in-[a,button]:cursor-pointer in-[a:hover,button:hover]:text-fg-base data-[status=error]:text-fg-error-icon data-[status=error]:in-[a:hover,button:hover]:text-fg-error-icon data-[status=pending]:opacity-60 motion-safe:data-[status=running]:*:data-[slot=trace-title]:text-shimmer",
        className,
      )}
      {...props}
    />
  )
}

function TraceIcon({ className, ...props }: React.ComponentProps<"span">) {
  return (
    <span
      data-slot="trace-icon"
      aria-hidden="true"
      className={cn(
        "flex size-4 shrink-0 items-center justify-center [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-3.5 [[data-slot=trace-title]~&]:-ml-1",
        className,
      )}
      {...props}
    />
  )
}

function TraceTitle({ className, ...props }: React.ComponentProps<"span">) {
  return <span data-slot="trace-title" className={cn("min-w-0 truncate", className)} {...props} />
}

function TraceDescription({ className, ...props }: React.ComponentProps<"span">) {
  return (
    <span
      data-slot="trace-description"
      className={cn("min-w-0 shrink-[100] truncate text-fg-muted/70 tabular-nums", className)}
      {...props}
    />
  )
}

type CollapsibleRootProps = React.ComponentProps<typeof CollapsiblePrimitive.Root>

function TraceCollapsible({
  className,
  ...props
}: Omit<CollapsibleRootProps, "asChild" | "open"> &
  (
    | { open?: undefined }
    | { open: boolean; onOpenChange: NonNullable<CollapsibleRootProps["onOpenChange"]> }
  )) {
  return (
    <CollapsiblePrimitive.Root
      data-slot="trace-collapsible"
      className={cn("flex w-full min-w-0 flex-col", className)}
      {...props}
    />
  )
}

function TraceCollapsibleTrigger({
  className,
  children,
  ...props
}: Omit<React.ComponentProps<typeof CollapsiblePrimitive.Trigger>, "asChild">) {
  return (
    <CollapsiblePrimitive.Trigger
      data-slot="trace-collapsible-trigger"
      className={cn(
        "group/trace-collapsible-trigger flex w-fit max-w-full min-w-0 cursor-pointer items-center gap-1 rounded-md text-left text-fg-muted transition-colors outline-hidden select-none hover:text-fg-base focus-visible:shadow-borders-interactive-with-shadow has-[[data-status=error]]:text-fg-error-icon",
        className,
      )}
      {...props}
    >
      {children}
      <TraceIcon className="text-fg-muted transition-transform duration-200 ease-out group-aria-expanded/trace-collapsible-trigger:rotate-90 motion-reduce:transition-none">
        <CaretRightIcon className="size-3" />
      </TraceIcon>
    </CollapsiblePrimitive.Trigger>
  )
}

function TraceCollapsibleContent({
  className,
  children,
  ...props
}: Omit<React.ComponentProps<typeof CollapsiblePrimitive.Content>, "asChild">) {
  return (
    <CollapsiblePrimitive.Content
      data-slot="trace-collapsible-content"
      className={cn(
        "min-w-0 overflow-hidden [--radix-accordion-content-height:var(--radix-collapsible-content-height)] motion-safe:data-[state=closed]:animate-accordion-up motion-safe:data-[state=open]:animate-accordion-down",
        className,
      )}
      {...props}
    >
      <div
        data-slot="trace-collapsible-content-inner"
        className="mt-2 mb-1 ml-[calc(--spacing(2)-1px)] min-w-0 border-l border-border-base pr-1 pl-4 text-xs leading-relaxed wrap-break-word text-fg-muted"
      >
        {children}
      </div>
    </CollapsiblePrimitive.Content>
  )
}

export {
  Trace,
  TraceCollapsible,
  TraceCollapsibleContent,
  TraceCollapsibleTrigger,
  TraceDescription,
  TraceIcon,
  TraceLine,
  TraceTitle,
}
export type { TraceStatus }
